import { Box } from "@mui/material";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useWeekViewContext } from "../../../contexts/WeekView/WeekViewContext";
import { useShownWeek } from "../../../hooks/useShownWeek";
import { getWeekStart } from "../../../utils/dateUtils";
import { DEFAULT_WEEK_START_DAY } from "../../../utils/models";
import { toIsoDate } from "../../../utils/scheduleWeeks";
import { staffingDatePathForRoute } from "../../../utils/staffingRoutes";
import { usePerformGlobalRefresh } from "../../header/hooks";
import { ScheduleHolidaysProvider } from "../../team/Holidays/schedule/ScheduleHolidaysProvider";
import { PullToRefresh } from "../../UIToolkit/PullToRefresh/PullToRefresh";
import { WeekScheduleLoadState } from "../WeekSchedule/WeekScheduleLoadState";
import { MobileStaffingContent } from "./MobileStaffingContent";
import { MobileStaffingHeader } from "./MobileStaffingHeader";
import { StaffingActionFab } from "./StaffingActionFab";

export const MobileStaffing = () => {
	const { dateString } = useParams();
	const { chosenDepartment } = useHospitalData();
	const { firstDate, lastDate } = useWeekViewContext();
	const { weekStart: shownWeekStart, goToWeek } = useShownWeek();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const [selectedDate, setSelectedDate] = useState<DateTime>(dateString ? DateTime.fromISO(dateString) : DateTime.now());

	// By calendar date: the URL's day is the browser's midnight, the schedule's the department's. Flag off the range is the
	// old list's loaded weeks; with `staffing_schedule_cache` on, the whole schedule.
	const isInSchedule = useMemo<boolean>(
		() =>
			selectedDate.isValid &&
			firstDate !== undefined &&
			lastDate !== undefined &&
			toIsoDate(firstDate) <= toIsoDate(selectedDate) &&
			toIsoDate(selectedDate) <= toIsoDate(lastDate),
		[selectedDate, firstDate, lastDate],
	);
	const isDateWeekShown = shownWeekStart !== undefined && toIsoDate(shownWeekStart) === toIsoDate(getWeekStart(selectedDate, DEFAULT_WEEK_START_DAY));

	const scrollRef = useRef<HTMLDivElement | null>(null);
	const childrenContainerRef = useRef<HTMLDivElement | null>(null);
	const [staffingHeaderHeight, setStaffingHeaderHeight] = useState<number>(0);
	const { performGlobalRefresh } = usePerformGlobalRefresh({});

	const handleDateChange = useCallback(
		(newDate: DateTime) => {
			setSelectedDate(newDate);
			// Stay on the route we are ON. `staffing/classic` and `staffing2` are pinned to this screen in
			// the route table, while the bare `staffing/:dateString?` is the URL the gaps-first switch owns
			// — so hardcoding the bare shape here let a classic screen navigate itself into the gaps shell
			// on a day change, and via the out-of-range redirect below, without the user touching anything.
			navigate(staffingDatePathForRoute(pathname, chosenDepartment.department_id, newDate.toFormat("yyyy-MM-dd")));
		},
		[navigate, pathname, chosenDepartment.department_id],
	);

	// The header and the list read the shown week's day items, so the shown week follows the day.
	useEffect(() => {
		if (isInSchedule && !isDateWeekShown) goToWeek(selectedDate);
	}, [isInSchedule, isDateWeekShown, selectedDate, goToWeek]);

	useEffect(() => {
		// `selectedDate` comes straight from the URL and is never checked against the schedule, so a shared/stale link,
		// a notification deep link, or a malformed date names a day with no week to show. Redirect onto the nearest day
		// of the schedule instead. That day is inside it, so this cannot re-fire. Nothing happens while the range is unknown.
		if (isInSchedule || firstDate === undefined || lastDate === undefined) return;

		// DEFAULT: an out-of-range or unparseable URL date falls back to the nearest range boundary, and an
		// unparseable one is treated like a date that precedes the range (an invalid DateTime compares false
		// against everything, so it cannot be placed). This does not mask missing data — the schedule loaded
		// fine, only the requested date is not in it, and the snackbar in MobileStaffingHeader says so.
		handleDateChange(!selectedDate.isValid || toIsoDate(selectedDate) < toIsoDate(firstDate) ? firstDate : lastDate);
	}, [isInSchedule, firstDate, lastDate, selectedDate, handleDateChange]);

	return (
		<ScheduleHolidaysProvider firstDate={firstDate} lastDate={lastDate}>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "100%",
					width: "100%",
					py: 2,
				}}
			>
				<MobileStaffingHeader date={selectedDate} setDate={handleDateChange} onRootHeightChange={(height) => setStaffingHeaderHeight(height)} />
				<Box
					id={"scrollable-staffing-container"}
					ref={scrollRef}
					style={{
						overflowY: "auto",
						overscrollBehaviorY: "contain",
						WebkitOverflowScrolling: "touch",
						background: "#f8f8f8",
					}}
					sx={{
						height: `calc(100% - ${staffingHeaderHeight}px)`,
						position: "relative",
						width: "100%",
					}}
				>
					<PullToRefresh
						key={"mobile-staffing-pull-to-refresh"}
						childrenContainerEl={childrenContainerRef.current}
						scrollerEl={scrollRef.current}
						onRefresh={performGlobalRefresh}
					/>
					<Box ref={childrenContainerRef} id={"mobile-staffing-content-box"} sx={{ pt: 2 }}>
						<WeekScheduleLoadState waitsForStaffList={false}>
							<MobileStaffingContent date={selectedDate} />
						</WeekScheduleLoadState>
					</Box>
				</Box>
				<StaffingActionFab date={selectedDate.toFormat("yyyy-MM-dd")} />
			</Box>
		</ScheduleHolidaysProvider>
	);
};
